"use client";

import Link from "next/link";
import React from "react";
import { Parallax } from "react-scroll-parallax";

import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/hooks/useTranslation";
import type { Contact } from "@/payload-types";
import type { PayLoadData } from "@/utils/payloadUtil";

import CustomImage from "../shared/CustomImage";

const Maps = ({ contact }: { contact: PayLoadData<Contact> | undefined }) => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  return (
    <section id="maps" className="bg-white py-0">
      <div className="relative flex h-full flex-col lg:h-[40rem] lg:flex-row">
        {/* Left: Headline + Button (trong container) */}

        <div className="relative flex w-full justify-center overflow-hidden py-28 sm:aspect-video md:h-full md:py-0 lg:w-1/2">
          <Parallax scale={[1, 2]} className="absolute inset-0 size-full">
            <CustomImage
              src="/bg_maps.jpeg"
              alt="Parallax Image"
              fill
              className="object-cover object-center"
              priority
            />
          </Parallax>
          <Parallax
            opacity={[0, 2]}
            className="absolute left-0 top-0 size-full bg-primary-800/85"
          />
          <Parallax
            speed={-10}
            className="container relative z-10 flex flex-col items-center justify-center px-4"
          >
            <h2 className="mb-8 text-center text-3xl font-bold !leading-normal text-white md:max-w-lg md:text-4xl">
              {t({
                de: "Freuen Sie sich auf magische Urlaubsmomente am Fuschlsee",
                en: "Now your magical holiday at Lake Fuschl can begin",
              })}
            </h2>
            <Link
              href="/booking"
              className="group inline-flex items-center justify-center rounded-full border border-white px-6 py-2.5 font-medium text-white transition-transform duration-500"
            >
              <span>{t({ de: "Jetzt Anfragen", en: "Book now" })}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="ml-2 size-5 transition-transform duration-300 group-hover:translate-x-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </Link>
          </Parallax>
        </div>
        {/* Right: Google Map */}
        <div className="iframe-full block aspect-square h-[unset] w-full bg-primary-500 sm:aspect-video lg:h-full lg:w-1/2">
          {(() => {
            const rawIframe = contact?.[language].mapEmbedCode || "";

            let cleanedIframe = rawIframe
              .replace(/width="\d+"/, 'width="100%"')
              .replace(/height="\d+"/, 'height="100%"');

            cleanedIframe = cleanedIframe.replace(
              /src="([^"]+)"/,
              (match, src) => {
                let newSrc = src;

                if (/!5m2!1s[a-z]{2}!2s/.test(newSrc)) {
                  newSrc = newSrc.replace(
                    /!5m2!1s[a-z]{2}!2s/,
                    `!5m2!1s${language}!2s`
                  );
                } else {
                  newSrc += `${newSrc.includes("?") ? "&" : "?"}hl=${language}`;
                }

                return `src="${newSrc}"`;
              }
            );

            return rawIframe ? (
              <div
                key={language}
                className="size-full"
                dangerouslySetInnerHTML={{ __html: cleanedIframe }}
              />
            ) : (
              <div className="flex size-full items-center justify-center text-gray-500">
                {t({
                  de: "Kartendaten sind leer!",
                  en: "Maps data is empty !",
                })}
              </div>
            );
          })()}
        </div>
      </div>
    </section>
  );
};

export default Maps;
